import CollapsibleSection from 'lib/ui/ConfigPanel/CollapsibleSection'
import Slider from 'lib/ui/Slider'
import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import React, {useState} from 'react'
import {Controller} from 'react-hook-form'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import ImageAssetUploader from 'lib/asset/ImageAssetUploader'
import ColorPicker from 'lib/ui/ColorPicker'
import MarginPaddingInputs from 'lib/ui/MarginPaddingInputs'
import {onUnknownChangeHandler} from 'lib/dom'
import {REMOVE} from 'Event/TemplateUpdateProvider'
import FontSelect from 'lib/FontSelect'
import {Label} from 'lib/ui/typography'
import VisibilitySetting from 'organization/Marketplace/config/BlockConfig/VisibilitySettings'
import TextEditor, {TextEditorContainer} from 'lib/ui/form/TextEditor'
import {TitleBlock} from 'Event/Marketplace/Block/Title'
import {useConfigurableSection} from 'organization/Marketplace/config/ConfigurableSection'
import MoveSectionButtons from 'organization/Marketplace/config/BlockConfig/MoveSectionButtons'
import {useSection} from 'Event/Marketplace/Section'

interface TitleConfigProps extends TitleBlock {
  id: string
  sectionId: string
  onMoveSectionUp?: () => void
  onMoveSectionDown?: () => void
}

export default function TitleConfig(props: TitleConfigProps) {
  const {
    text,
    id,
    padding,
    alignment,
    background,
    font,
    fontSize,
    color,
    lineHeight,
    letterSpacing,
    onMoveSectionUp,
    onMoveSectionDown,
  } = props

  const {
    form: {control},
  } = useConfig()

  const [visibility, setVisibility] = useState(props.visibility)

  const {update} = useConfigurableSection()
  const {calculateVisibility} = useSection()

  const handleRemove = () => {
    update({
      blocks: {
        [id]: REMOVE,
      },
    })
    calculateVisibility()
  }

  const save = (data: any) => {
    data.visibility = visibility
    update({
      blocks: {
        [id]: data,
      },
    })
    calculateVisibility()
  }

  return (
    <Config title="Title Block" onSave={save} onRemove={handleRemove}>
      <SettingsPanel>
        <Controller
          name="text"
          defaultValue={text}
          control={control}
          render={({value, onChange}) => (
            <TextEditorContainer>
              <TextEditor
                aria-label="text"
                data={value}
                onChange={onChange}
                toolbar={[
                  'bold',
                  'italic',
                  'underline',
                  'fontColor',
                  'removeFormat',
                ]}
              />
            </TextEditorContainer>
          )}
        />
        <VisibilitySetting
          visibility={visibility}
          setVisibility={setVisibility}
        />
        <MoveSectionButtons
          onMoveUp={onMoveSectionUp}
          onMoveDown={onMoveSectionDown}
        />
      </SettingsPanel>
      <StylingPanel>
        <CollapsibleSection
          label="Typography"
          storageKey={`blocks-${id}-typography`}
        >
          <Label>Font</Label>
          <Controller
            control={control}
            name="font"
            defaultValue={font}
            render={({value, onChange}) => (
              <FontSelect value={value} onChange={onChange} />
            )}
          />
          <Controller
            name="fontSize"
            defaultValue={fontSize}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={1}
                max={100}
                onChange={onChange}
                valueLabelDisplay="off"
                value={value}
                label="Font Size"
              />
            )}
          />
          <Controller
            name="color"
            control={control}
            defaultValue={color}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />

          <Controller
            name={`lineHeight`}
            defaultValue={lineHeight}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={300}
                unit="%"
                onChange={onChange}
                valueLabelDisplay="off"
                value={value}
                label="Line Height"
              />
            )}
          />

          <Controller
            name={`letterSpacing`}
            defaultValue={letterSpacing}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={-5}
                max={10}
                onChange={onChange}
                valueLabelDisplay="off"
                value={value}
                label="Letter Spacing"
              />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Background"
          storageKey={`blocks-${id}-background`}
        >
          <Controller
            name={`background.image`}
            control={control}
            defaultValue={background.image}
            render={({onChange, value}) => (
              <ImageAssetUploader
                onChange={onChange}
                value={value}
                uploadLabel="Upload"
                uploadInputProps={{
                  'aria-label': 'block background image',
                }}
                width={1920}
                height={1200}
                canResize
                disableAutoRemove
              />
            )}
          />
          <Controller
            name={`background.color`}
            defaultValue={background.color}
            control={control}
            render={({value, onChange}) => (
              <ColorPicker
                label="Color"
                color={value}
                onPick={onChange}
                aria-label="block background color"
              />
            )}
          />
          <Controller
            name={`background.opacity`}
            defaultValue={background.opacity}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Opacity"
                unit="%"
                valueLabelDisplay="auto"
                aria-label="block background opacity"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
              />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection label="Border" storageKey={`blocks-${id}-border`}>
          <Controller
            name={`border.width`}
            defaultValue={props.border.width}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={50}
                step={1}
                onChange={onChange}
                value={value}
                label="Thickness"
              />
            )}
          />
          <Controller
            name={`border.radius`}
            defaultValue={props.border.radius}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={50}
                step={1}
                onChange={onChange}
                value={value}
                label="Radius"
              />
            )}
          />
          <Controller
            name={`border.color`}
            defaultValue={props.border.color}
            control={control}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Alignment"
          storageKey={`blocks-${id}-alignment`}
        >
          <Controller
            name={`alignment.vertical`}
            defaultValue={alignment.vertical}
            control={control}
            render={({value, onChange}) => (
              <Select
                fullWidth
                label="Vertical"
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
              >
                <Option value="start">Top</Option>
                <Option value="center">Center</Option>
                <Option value="end">Bottom</Option>
              </Select>
            )}
          />
          <Controller
            name={`alignment.horizontal`}
            defaultValue={alignment.horizontal}
            control={control}
            render={({value, onChange}) => (
              <Select
                fullWidth
                label="Horizontal"
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
              >
                <Option value="left">Left</Option>
                <Option value="center">Center</Option>
                <Option value="right">Right</Option>
              </Select>
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Advanced Spacing"
          storageKey={`blocks-${id}-spacing`}
        >
          <Label>Padding</Label>
          <MarginPaddingInputs
            control={control}
            namePrefix={`padding`}
            values={padding}
          />
        </CollapsibleSection>
      </StylingPanel>
    </Config>
  )
}
